import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * Auto-height blocks report their content height here; the canvas uses it as the block's layout
 * height (react-grid-layout needs an explicit height per item).
 */
export const BlockHeightReportContext = createContext<((blockId: string, px: number) => void) | null>(null);

/**
 * Measures an element's natural height and reports it for `blockId` while `enabled`. Attach the
 * returned ref to a wrapper whose height comes from its content, never from the grid item, or the
 * measurement would feed back into itself.
 */
export function useReportBlockHeight<T extends HTMLElement>(blockId: string, enabled: boolean, extraPx = 0) {
  const report = useContext(BlockHeightReportContext);
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !report || !el) return;
    const send = () => report(blockId, Math.ceil(el.getBoundingClientRect().height) + extraPx);
    send();
    const observer = new ResizeObserver(send);
    observer.observe(el);
    return () => observer.disconnect();
  }, [blockId, enabled, report, extraPx]);

  return ref;
}

/** Clamps a height to a block's or section's optional px floor and ceiling. */
export function clampHeight(px: number, min: number | undefined, max: number | undefined): number {
  let h = px;
  if (min !== undefined) h = Math.max(h, min);
  if (max !== undefined && max > 0) h = Math.min(h, max);
  return h;
}
